import { RiBookOpenLine, RiFolderLine } from '@remixicon/react';

import {
  type ToolGroupEntry,
  ToolLabel,
  type ToolRenderOptions,
  createToolExtension,
} from '@spotify/plugin-confidence-chat-react';

import { confidenceChatPlugin } from '../plugin';
import { ToolResponseDetails, ToolTextDetails } from './ToolResponseDetails';
import { formatLineCount } from './formatLineCount';
import {
  formatToolResultMessages,
  getInputString,
  getToolInput,
  parseToolResultObject,
  truncateToolSubject,
} from './parseToolResult';

type ToolConfig = {
  running: string;
  completed: string;
  failed: string;
  subjectKey: string;
  conceptContent?: boolean;
  countKey?: string;
  countNoun?: string;
};

const conceptTools: Record<string, ToolConfig> = {
  mcp__knowledge__get_concept: {
    running: 'Reading concept',
    completed: 'Read concept',
    failed: 'Failed to read concept',
    subjectKey: 'concept_id',
    conceptContent: true,
  },
  mcp__knowledge__write_concept: {
    running: 'Writing concept',
    completed: 'Wrote concept',
    failed: 'Failed to write concept',
    subjectKey: 'concept_id',
    conceptContent: true,
  },
  mcp__knowledge__edit_concept: {
    running: 'Editing concept',
    completed: 'Edited concept',
    failed: 'Failed to edit concept',
    subjectKey: 'concept_id',
    conceptContent: true,
  },
  mcp__knowledge__list_concepts: {
    running: 'Listing concepts',
    completed: 'Listed concepts',
    failed: 'Failed to list concepts',
    subjectKey: 'bundle',
    countKey: 'concepts',
    countNoun: 'concept',
  },
  mcp__knowledge__search_concepts: {
    running: 'Searching concepts',
    completed: 'Searched concepts',
    failed: 'Failed to search concepts',
    subjectKey: 'query',
    countKey: 'hits',
    countNoun: 'concept',
  },
};

const bundleTools: Record<string, ToolConfig> = {
  mcp__knowledge__create_bundle: {
    running: 'Creating bundle',
    completed: 'Created bundle',
    failed: 'Failed to create bundle',
    subjectKey: 'bundle_id',
  },
  mcp__knowledge__list_bundles: {
    running: 'Listing bundles',
    completed: 'Listed bundles',
    failed: 'Failed to list bundles',
    subjectKey: 'filter',
    countKey: 'bundles',
    countNoun: 'bundle',
  },
  mcp__knowledge__update_bundle: {
    running: 'Updating bundle',
    completed: 'Updated bundle',
    failed: 'Failed to update bundle',
    subjectKey: 'bundle',
  },
};

const KnowledgeToolLabel = ({
  entry,
  options,
}: {
  entry: ToolGroupEntry;
  options: ToolRenderOptions;
}) => {
  const config = conceptTools[entry.tool] ?? bundleTools[entry.tool];
  const input = getToolInput(entry.input);
  const result = entry.result?.success
    ? parseToolResultObject(entry.result.content)
    : undefined;
  const subject =
    (config.conceptContent
      ? getInputString(result ?? {}, 'title')
      : undefined) ||
    (config.conceptContent ? getInputString(input, 'title') : undefined) ||
    getInputString(input, config.subjectKey) ||
    getInputString(result ?? {}, 'name');
  let action = config.running;
  if (!options.isStreaming && entry.result) {
    action = entry.result.success ? config.completed : config.failed;
  }
  const inputContent = getInputString(input, 'content', {
    preserveWhitespace: true,
  });
  const body = getInputString(result ?? {}, 'body', {
    preserveWhitespace: true,
  });
  const content = config.conceptContent ? (body ?? inputContent) : undefined;
  const items = config.countKey ? result?.[config.countKey] : undefined;
  let summary: string | undefined;
  if (entry.result) {
    if (!entry.result.success) {
      summary = 'Failed';
    } else if (Array.isArray(items)) {
      summary = `${items.length}${result?.truncated === true ? '+' : ''} ${config.countNoun}${items.length === 1 ? '' : 's'}`;
    } else if (content !== undefined) {
      summary = formatLineCount(content);
    }
  }
  const streamingTail =
    options.isStreaming && config.conceptContent && inputContent
      ? inputContent.slice(-7)
      : undefined;
  const showContent = content !== undefined && entry.result?.success !== false;
  const responseMessages = entry.result
    ? formatToolResultMessages(entry.result.content)
    : '';
  const conceptMetadata =
    config.conceptContent && content !== undefined
      ? Object.fromEntries(
          Object.entries(result ?? input).filter(([key]) =>
            ['type', 'title', 'description', 'frontmatter'].includes(key),
          ),
        )
      : undefined;

  return (
    <ToolLabel.Root>
      <ToolLabel.Primary>
        {action}
        {subject && (
          <>
            {' '}
            <span title={subject}>{truncateToolSubject(subject)}</span>
          </>
        )}
      </ToolLabel.Primary>
      {(streamingTail || summary) && (
        <ToolLabel.Secondary>{streamingTail ?? summary}</ToolLabel.Secondary>
      )}
      <ToolLabel.Timer startedAt={entry.result ? undefined : entry.startedAt} />
      <ToolLabel.Details>
        {showContent ? (
          <>
            <ToolTextDetails content={content || 'Empty concept'} />
            {responseMessages && <ToolTextDetails content={responseMessages} />}
          </>
        ) : (
          <ToolResponseDetails
            content={entry.result?.content || JSON.stringify(input, null, 2)}
          />
        )}
        {entry.result?.success !== false &&
          conceptMetadata &&
          Object.keys(conceptMetadata).length > 0 && (
            <ToolTextDetails
              content={JSON.stringify(conceptMetadata, null, 2)}
            />
          )}
      </ToolLabel.Details>
    </ToolLabel.Root>
  );
};

export const ConceptToolExtension = confidenceChatPlugin.provide(
  createToolExtension({
    id: 'knowledge-concept-operations',
    predicate: tool => Object.hasOwn(conceptTools, tool),
    icon: RiBookOpenLine,
    display: 'grouped',
    collapseConsecutiveCalls: false,
    renderLabel: (entry, options) => (
      <KnowledgeToolLabel entry={entry} options={options} />
    ),
    render: () => null,
  }),
);

export const BundleToolExtension = confidenceChatPlugin.provide(
  createToolExtension({
    id: 'knowledge-bundle-operations',
    predicate: tool => Object.hasOwn(bundleTools, tool),
    icon: RiFolderLine,
    display: 'grouped',
    collapseConsecutiveCalls: false,
    renderLabel: (entry, options) => (
      <KnowledgeToolLabel entry={entry} options={options} />
    ),
    render: () => null,
  }),
);
